import React from "react";
import { Tooltip } from "antd";
import { Scissors, RotateCcw } from "lucide-react";

/** Tintas das tags de assunto — determinísticas, na paleta de acentos do produto. */
export const TAG_TINTS = [
  "bg-accent-blue/[0.14] text-accent-blue-ink",
  "bg-accent-purple/[0.14] text-accent-purple-ink",
  "bg-accent-green/[0.14] text-accent-green-ink",
  "bg-accent-orange/[0.14] text-accent-orange-ink",
  "bg-accent-cyan/[0.14] text-accent-cyan-ink",
];

const ROMANO = /^(?:I|II|III|IV|V|VI|VII|VIII|IX|X|XI|XII)$/;

type SegmentoEnunciado =
  | { tipo: "md"; texto: string }
  | { tipo: "item"; numeral: string; texto: string; indice: number };

/**
 * Quebra o enunciado em blocos de markdown e itens riscáveis: cada linha que
 * começa com numeral romano (I -, II -, III …) vira um item com tesoura
 * própria. O item segue a regra de parágrafo do markdown — linhas seguintes
 * continuam pertencendo a ele até uma linha em branco ou o próximo numeral.
 * Com menos de dois itens não é uma lista de assertivas — o enunciado volta a
 * ser renderizado como markdown puro.
 */
export function parseEnunciado(statement: string): SegmentoEnunciado[] {
  const segmentos: SegmentoEnunciado[] = [];
  let buffer: string[] = [];
  let indice = 0;
  let continuandoItem = false;

  const flush = () => {
    if (buffer.some((l) => l.trim()))
      segmentos.push({ tipo: "md", texto: buffer.join("\n") });
    buffer = [];
  };

  for (const linha of statement.split(/\r?\n/)) {
    const m = linha.match(/^\s*([IVX]+)\s*[-–—.)]\s+(.+)$/);
    if (m && ROMANO.test(m[1])) {
      flush();
      segmentos.push({ tipo: "item", numeral: m[1], texto: m[2], indice: indice++ });
      continuandoItem = true;
    } else if (continuandoItem && linha.trim()) {
      const ultimo = segmentos[segmentos.length - 1];
      if (ultimo?.tipo === "item") ultimo.texto += " " + linha.trim();
    } else {
      continuandoItem = false;
      buffer.push(linha);
    }
  }
  flush();

  if (segmentos.filter((s) => s.tipo === "item").length < 2) {
    return [{ tipo: "md", texto: statement }];
  }
  return segmentos;
}

const cx = (...v: Array<string | false | null | undefined>) => v.filter(Boolean).join(" ");

/**
 * Tesoura ao lado de uma alternativa ou item do enunciado. No desktop fica
 * invisível até o hover na linha (classe `group/riscavel` no pai da linha) —
 * e a linha inteira acende junto, mostrando exatamente o que a tesoura vai
 * riscar. O espaço continua reservado para a largura não pular; no mobile é
 * sempre visível.
 */
export function ScissorButton({
  cut,
  onClick,
  cutLabel,
  restoreLabel,
  size = "md",
  alwaysVisible = false,
}: {
  cut: boolean;
  onClick: () => void;
  cutLabel: string;
  restoreLabel: string;
  size?: "sm" | "md" | "lg";
  alwaysVisible?: boolean;
}) {
  const dims = size === "sm" ? "h-8 w-8" : size === "lg" ? "h-10 w-10" : "h-9 w-9";
  return (
    <Tooltip title={cut ? restoreLabel : cutLabel} placement="left">
      <button
        type="button"
        aria-label={cut ? restoreLabel : cutLabel}
        aria-pressed={cut}
        onClick={onClick}
        className={cx(
          "flex shrink-0 items-center justify-center rounded-full border bg-surface",
          "transition-all duration-150 motion-reduce:transition-none",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60",
          dims,
          cut
            ? "border-edge-strong text-ink-secondary hover:bg-surface-hover hover:text-ink"
            : "border-edge-strong text-ink-secondary hover:border-danger/40 hover:bg-danger/[0.10] hover:text-danger-ink",
          !cut &&
            !alwaysVisible &&
            "opacity-0 focus-visible:opacity-100 group-hover/riscavel:opacity-100",
        )}
      >
        {cut ? <RotateCcw size={15} aria-hidden /> : <Scissors size={15} aria-hidden />}
      </button>
    </Tooltip>
  );
}
